import { z } from "zod";
import { IdPageQuery, IsoDateTime, Uuid, pageOf } from "./common";

const Currency = z.string().regex(/^[A-Za-z]{3}$/).transform((value) => value.toUpperCase());
const Minor = z.number().int().positive().max(1_000_000_000_000);
const Language = z.enum(["ro", "bg", "hu", "en"]);
const Country = z.string().regex(/^[A-Za-z]{2}$/).transform((value) => value.toUpperCase());
const HttpsUrl = z.url().max(1_500).refine((value) => value.startsWith("https://"), { message: "https only" });
const RequestKey = z.string().trim().min(8).max(100);
const Name = z.string().trim().min(1).max(200);
const ExternalId = z.string().regex(/^\d{1,32}$/);

const ProductKey = z.string().trim().min(1).max(200);

// ---------------------------------------------------------------------------
// Schedule of the week. Days are ISO, as in the sales feed: 1 is Monday, 7 is Sunday. Hours are the account's.
// ---------------------------------------------------------------------------

export const AdScheduleWindow = z
  .strictObject({
    days: z.array(z.number().int().min(1).max(7)).min(1).max(7),
    startHour: z.number().int().min(0).max(23),
    endHour: z.number().int().min(1).max(24),
  })
  .refine((window) => window.endHour > window.startHour, { message: "a window ends after it starts" })
  .refine((window) => new Set(window.days).size === window.days.length, { message: "each day once" });
export type AdScheduleWindow = z.infer<typeof AdScheduleWindow>;
export const AdSchedule = z.array(AdScheduleWindow).min(1).max(6);
export type AdSchedule = z.infer<typeof AdSchedule>;

const FourTexts = z.object({ ro: z.string(), bg: z.string(), hu: z.string(), en: z.string() });

export const AdScheduleReason = z.object({ code: z.string(), params: z.record(z.string(), z.unknown()), texts: FourTexts });
export type AdScheduleReason = z.infer<typeof AdScheduleReason>;
export const AdScheduleSuggestionDto = z.object({
  /** Null when there are no sales to read: nothing is made up. */
  schedule: AdSchedule.nullable(),
  reason: AdScheduleReason,
});
export type AdScheduleSuggestionDto = z.infer<typeof AdScheduleSuggestionDto>;

// ---------------------------------------------------------------------------
// Availability
// ---------------------------------------------------------------------------

export const AD_HOLD_STATUSES = ["holding", "back_in_stock", "restarted", "dismissed", "pause_failed"] as const;
export const AdHoldStatus = z.enum(AD_HOLD_STATUSES);
export type AdHoldStatus = z.infer<typeof AdHoldStatus>;
export const AD_HOLD_SUBJECTS = ["cell", "rsa", "wizard", "promotion"] as const;
export const AdHoldSubject = z.enum(AD_HOLD_SUBJECTS);
export type AdHoldSubject = z.infer<typeof AdHoldSubject>;
export const AdHoldDto = z.object({
  id: Uuid,
  connectionId: Uuid,
  productKey: z.string(),
  productName: z.string(),
  subjectKind: AdHoldSubject,
  subjectId: Uuid,
  level: z.string(),
  entityId: Uuid,
  entityName: z.string(),
  status: AdHoldStatus,
  outcome: z.string(),
  actionId: Uuid.nullable(),
  expectedBackAt: IsoDateTime.nullable(),
  pausedAt: IsoDateTime,
  backAt: IsoDateTime.nullable(),
  restartedAt: IsoDateTime.nullable(),
  errorCode: z.string().nullable(),
  /** The line of the journal, readable. */
  texts: FourTexts,
  /** Back in stock: a person may start it again. It does not start by itself. */
  canStart: z.boolean(),
});
export type AdHoldDto = z.infer<typeof AdHoldDto>;
export const AdHoldPage = pageOf(AdHoldDto);
export const AdHoldListQuery = IdPageQuery.extend({ status: AdHoldStatus.optional() });
export const AdAvailabilityReportDto = z.object({
  unavailable: z.number().int(),
  paused: z.number().int(),
  dryRun: z.number().int(),
  failed: z.number().int(),
  backInStock: z.number().int(),
});
export type AdAvailabilityReportDto = z.infer<typeof AdAvailabilityReportDto>;

// ---------------------------------------------------------------------------
// Account assets
// ---------------------------------------------------------------------------

export const AdAccountAssetsInput = z.strictObject({
  metaPageId: ExternalId.nullable().optional(),
  metaInstagramId: ExternalId.nullable().optional(),
  metaBusinessId: ExternalId.nullable().optional(),
  metaPixelId: ExternalId.nullable().optional(),
  dsaBeneficiary: z.string().trim().min(1).max(200).nullable().optional(),
  dsaPayor: z.string().trim().min(1).max(200).nullable().optional(),
});
export const AdAccountAssetsDto = z.object({
  connectionId: Uuid,
  metaPageId: z.string().nullable(),
  metaInstagramId: z.string().nullable(),
  metaBusinessId: z.string().nullable(),
  metaPixelId: z.string().nullable(),
  dsaBeneficiary: z.string().nullable(),
  dsaPayor: z.string().nullable(),
});
export type AdAccountAssetsDto = z.infer<typeof AdAccountAssetsDto>;
export const AdPageList = z.object({ items: z.array(z.object({ id: z.string(), name: z.string(), instagramUserId: z.string().nullable() })) });

// ---------------------------------------------------------------------------
// Test cells
// ---------------------------------------------------------------------------

export const AD_CREATION_STATUSES = ["draft", "dry_run", "creating", "paused", "unconfirmed", "failed", "running", "decided", "stopped", "finished"] as const;
export const AdCreationStatus = z.enum(AD_CREATION_STATUSES);
export type AdCreationStatus = z.infer<typeof AdCreationStatus>;
/** What an ad shows: the steps of any built ad, and one more for an ad the system stopped and a person has to look at. A row never stores it. */
export const AD_PROMOTION_STATUSES = [...AD_CREATION_STATUSES, "stopped_by_system"] as const;
export const AdPromotionStatus = z.enum(AD_PROMOTION_STATUSES);
export type AdPromotionStatus = z.infer<typeof AdPromotionStatus>;
/**
 * Why the system stopped it, or tried to. With status `stopped_by_system` the campaign is paused and spends nothing. With
 * status `running` and code `pause_failed` the stop did not land: the product ran out and the ad may still be spending.
 */
export const AdSystemStop = z.object({
  source: z.enum(["availability", "guard", "emergency"]),
  /** `product_unavailable`, `back_in_stock`, `pause_failed`, or the guard's reason, as on the campaign. */
  code: z.string(),
  productKey: z.string().nullable(),
  at: IsoDateTime.nullable(),
  /** False while a Start would be refused for certain: the product is out, or the emergency stop stands. A guard's limit is judged at the Start. */
  canStart: z.boolean(),
});
export type AdSystemStop = z.infer<typeof AdSystemStop>;
/** The name it had when only promotions could say this. */
export const AdPromotionSystemStop = AdSystemStop;
export type AdPromotionSystemStop = AdSystemStop;

export const AD_CELL_OBJECTIVES = ["website_leads", "instant_form"] as const;
export const AdCellObjective = z.enum(AD_CELL_OBJECTIVES);
export type AdCellObjective = z.infer<typeof AdCellObjective>;
export const AD_CELL_GOALS = ["cpl", "cpa"] as const;
export const AdCellGoal = z.enum(AD_CELL_GOALS);
export type AdCellGoal = z.infer<typeof AdCellGoal>;
export const AD_CELL_MIN_VARIANTS = 2;
export const AD_CELL_MAX_VARIANTS = 5;

export const AdTargeting = z.strictObject({
  countries: z.array(Country).min(1).max(25).default(["RO"]),
  ageMin: z.number().int().min(18).max(65).default(25),
  ageMax: z.number().int().min(18).max(65).default(65),
});
export type AdTargeting = z.infer<typeof AdTargeting>;

export const AdCellCreate = z
  .strictObject({
    connectionId: Uuid,
    requestKey: RequestKey,
    topicId: Uuid,
    creativeIds: z.array(Uuid).min(AD_CELL_MIN_VARIANTS).max(AD_CELL_MAX_VARIANTS),
    objective: AdCellObjective.default("website_leads"),
    goal: AdCellGoal.default("cpl"),
    leadFormId: Uuid.optional(),
    /** Per variant. Left out means the smallest test budget. */
    dailyBudgetMinor: Minor.optional(),
    currency: Currency.optional(),
    targeting: AdTargeting.optional(),
    destinationUrl: HttpsUrl.optional(),
    /** The product these ads sell: when it runs out, they stop. */
    productKey: ProductKey.optional(),
  })
  .refine((v) => new Set(v.creativeIds).size === v.creativeIds.length, { path: ["creativeIds"], message: "each creative once" })
  .refine((v) => v.objective !== "instant_form" || v.leadFormId !== undefined, { path: ["leadFormId"], message: "an instant form objective needs a form" });

export const AdCellPatch = z.strictObject({
  abcMode: z.enum(["advise", "stop_losers"]).optional(),
  autoScale: z.boolean().optional(),
  scaleCeilingMinor: Minor.optional(),
});

export const AD_VARIANT_VERDICTS = ["testing", "winner", "loser"] as const;

export const AdVariantDto = z.object({
  id: Uuid,
  letter: z.string(),
  creativeId: Uuid,
  hook: z.string(),
  headline: z.string().nullable(),
  finalUrl: z.string().nullable(),
  utmTerm: z.string(),
  utmContent: z.string(),
  adSetName: z.string(),
  adName: z.string(),
  externalAdSetId: z.string().nullable(),
  externalAdId: z.string().nullable(),
  adSetId: Uuid.nullable(),
  adId: Uuid.nullable(),
  verdict: z.enum(AD_VARIANT_VERDICTS),
  stoppedAt: IsoDateTime.nullable(),
});

export const AdCellDto = z.object({
  id: Uuid,
  connectionId: Uuid,
  code: z.string(),
  topicId: Uuid,
  angle: z.string(),
  objective: AdCellObjective,
  goal: AdCellGoal,
  currency: z.string(),
  dailyBudgetMinor: z.number().int(),
  status: AdPromotionStatus,
  /** With `stopped_by_system`, or `pause_failed` beside `running`: read from the mirror, as for a promotion. */
  systemStop: AdSystemStop.nullable(),
  externalCampaignId: z.string().nullable(),
  campaignId: Uuid.nullable(),
  abcMode: z.enum(["advise", "stop_losers"]),
  autoScale: z.boolean(),
  scaleCeilingMinor: z.number().int(),
  winnerVariantId: Uuid.nullable(),
  startedAt: IsoDateTime.nullable(),
  errorCode: z.string().nullable(),
  variants: z.array(AdVariantDto),
  productKey: z.string().nullable(),
  createdAt: IsoDateTime,
});
export type AdCellDto = z.infer<typeof AdCellDto>;
export const AdCellPage = pageOf(AdCellDto);
export const AdCellListQuery = IdPageQuery.extend({ status: AdCreationStatus.optional() });

export const AD_DECISION_KINDS = ["collecting", "keep_testing", "winner", "stop_loser", "scale_winner", "next_variant", "hold"] as const;
export const AdDecisionKind = z.enum(AD_DECISION_KINDS);
export type AdDecisionKind = z.infer<typeof AdDecisionKind>;
export const AD_DECISION_OUTCOMES = ["recorded", "applied", "dry_run", "refused", "failed", "proposed"] as const;
export const AdDecisionOutcome = z.enum(AD_DECISION_OUTCOMES);
export type AdDecisionOutcome = z.infer<typeof AdDecisionOutcome>;

export const AdCellDecisionDto = z.object({
  id: Uuid,
  cellId: Uuid,
  variantId: Uuid.nullable(),
  kind: AdDecisionKind,
  code: z.string(),
  params: z.unknown(),
  /** The reason, readable, in each language of the interface. */
  texts: FourTexts,
  metric: z.string().nullable(),
  stats: z.unknown(),
  outcome: AdDecisionOutcome,
  actionId: Uuid.nullable(),
  createdAt: IsoDateTime,
});
export type AdCellDecisionDto = z.infer<typeof AdCellDecisionDto>;
export const AdCellDecisionPage = pageOf(AdCellDecisionDto);

export const AdEvaluationDto = z.object({
  cellId: Uuid,
  decisions: z.array(AdCellDecisionDto),
});
export type AdEvaluationDto = z.infer<typeof AdEvaluationDto>;

// ---------------------------------------------------------------------------
// Responsive search ads
// ---------------------------------------------------------------------------

export const RsaAssetInput = z.strictObject({ text: z.string().trim().min(1).max(200), pin: z.number().int().min(1).max(3).nullable().optional() });
export type RsaAssetInput = z.infer<typeof RsaAssetInput>;
export const SitelinkInput = z.strictObject({
  text: z.string().trim().min(1).max(100),
  description1: z.string().trim().max(100).optional(),
  description2: z.string().trim().max(100).optional(),
  url: HttpsUrl,
});
export type SitelinkInput = z.infer<typeof SitelinkInput>;
export const RsaSpec = z.strictObject({
  headlines: z.array(RsaAssetInput).max(40),
  descriptions: z.array(RsaAssetInput).max(20),
  path1: z.string().trim().max(50).optional(),
  path2: z.string().trim().max(50).optional(),
});
export type RsaSpec = z.infer<typeof RsaSpec>;

export const AdFindingDto = z.object({ code: z.string(), severity: z.enum(["red", "yellow"]), field: z.string().optional(), params: z.unknown().optional() });
export type AdFindingDto = z.infer<typeof AdFindingDto>;

export const AdRsaCreate = z.strictObject({
  adSetId: Uuid,
  requestKey: RequestKey,
  topicId: Uuid,
  rsa: RsaSpec,
  finalUrl: HttpsUrl,
  language: Language.default("ro"),
  productKey: ProductKey.optional(),
});
export const AdRsaCheck = z.strictObject({ topicId: Uuid, rsa: RsaSpec, finalUrl: HttpsUrl.optional(), language: Language.default("ro") });
export const AdRsaCheckDto = z.object({ ok: z.boolean(), findings: z.array(AdFindingDto), combinations: z.number().int() });
export const AdRsaDto = z.object({
  id: Uuid,
  connectionId: Uuid,
  adSetId: Uuid,
  topicId: Uuid,
  status: AdCreationStatus,
  externalId: z.string().nullable(),
  adId: Uuid.nullable(),
  finalUrl: z.string(),
  urlSuffix: z.string(),
  utmContent: z.string(),
  errorCode: z.string().nullable(),
  createdAt: IsoDateTime,
});
export type AdRsaDto = z.infer<typeof AdRsaDto>;

// ---------------------------------------------------------------------------
// Instant forms
// ---------------------------------------------------------------------------

export const AD_FORM_TEMPLATES = ["contact", "quote", "booking", "offer"] as const;
export const AdFormTemplate = z.enum(AD_FORM_TEMPLATES);
export type AdFormTemplate = z.infer<typeof AdFormTemplate>;

export const AdFormQuestion = z.object({
  type: z.string(),
  key: z.string(),
  label: z.string().optional(),
  options: z.array(z.object({ key: z.string(), value: z.string() })).optional(),
});
export const AdFormTemplateDto = z.object({
  id: AdFormTemplate,
  name: z.string(),
  language: Language,
  intro: z.object({ title: z.string(), content: z.array(z.string()), button: z.string() }),
  questions: z.array(AdFormQuestion),
  thanks: z.object({ title: z.string(), body: z.string(), button: z.string() }),
  higherIntent: z.boolean(),
});
export type AdFormTemplateDto = z.infer<typeof AdFormTemplateDto>;
export const AdFormTemplateList = z.object({ items: z.array(AdFormTemplateDto) });
export const AdFormTemplateQuery = z.object({ language: Language.default("ro") });

export const AdFormCreate = z.strictObject({
  connectionId: Uuid,
  name: Name,
  template: AdFormTemplate,
  language: Language.default("ro"),
  pageId: ExternalId.optional(),
  privacyPolicyUrl: HttpsUrl,
  websiteUrl: HttpsUrl,
  higherIntent: z.boolean().optional(),
});
export const AdFormDto = z.object({
  id: Uuid,
  connectionId: Uuid,
  pageId: z.string(),
  name: z.string(),
  template: z.string(),
  language: z.string(),
  status: z.string(),
  externalId: z.string().nullable(),
  lastReadAt: IsoDateTime.nullable(),
  errorCode: z.string().nullable(),
  waitingLeads: z.number().int(),
  deliveredLeads: z.number().int(),
  createdAt: IsoDateTime,
});
export type AdFormDto = z.infer<typeof AdFormDto>;
export const AdFormPage = pageOf(AdFormDto);
export const AdFormIngestDto = z.object({
  forms: z.number().int(),
  read: z.number().int(),
  added: z.number().int(),
  delivered: z.number().int(),
  waiting: z.number().int(),
  failed: z.array(z.object({ formId: Uuid, code: z.string() })),
  expired: z.number().int(),
});
export type AdFormIngestDto = z.infer<typeof AdFormIngestDto>;

// ---------------------------------------------------------------------------
// Search wizard
// ---------------------------------------------------------------------------

export const AD_KEYWORD_MATCHES = ["EXACT", "PHRASE"] as const;
export const AdWizardKeyword = z.strictObject({ text: z.string().trim().min(1).max(80), match: z.enum(AD_KEYWORD_MATCHES).default("PHRASE") });
export type AdWizardKeyword = z.infer<typeof AdWizardKeyword>;

export const AdWizardCreate = z.strictObject({
  connectionId: Uuid,
  topicId: Uuid,
  name: Name,
  dailyBudgetMinor: Minor.optional(),
  cpcCeilingMinor: Minor.optional(),
  currency: Currency.optional(),
  locations: z.array(z.string().regex(/^\d{1,12}$/)).min(1).max(25).optional(),
  languages: z.array(Language).min(1).max(4).default(["ro"]),
  destinationUrl: HttpsUrl.optional(),
  productKey: ProductKey.optional(),
  /** Left out: prefilled from the sales feed. Null: at all hours. */
  schedule: AdSchedule.nullable().optional(),
});
export const AdWizardPatch = z.strictObject({
  name: Name.optional(),
  dailyBudgetMinor: Minor.optional(),
  cpcCeilingMinor: Minor.optional(),
  locations: z.array(z.string().regex(/^\d{1,12}$/)).min(1).max(25).optional(),
  languages: z.array(Language).min(1).max(4).optional(),
  keywords: z.array(AdWizardKeyword).max(200).optional(),
  negatives: z.array(z.string().trim().min(1).max(80)).max(500).optional(),
  sharedNegativeIds: z.array(z.string().regex(/^\d{1,20}$/)).max(20).optional(),
  rsa: RsaSpec.optional(),
  sitelinks: z.array(SitelinkInput).max(8).optional(),
  destinationUrl: HttpsUrl.optional(),
  productKey: ProductKey.nullable().optional(),
  schedule: AdSchedule.nullable().optional(),
});
export const AdWizardDto = z.object({
  id: Uuid,
  connectionId: Uuid,
  code: z.string(),
  name: z.string(),
  topicId: Uuid,
  status: AdPromotionStatus,
  /** With `stopped_by_system`, or `pause_failed` beside `running`: read from the mirror, as for a promotion. */
  systemStop: AdSystemStop.nullable(),
  currency: z.string(),
  dailyBudgetMinor: z.number().int(),
  cpcCeilingMinor: z.number().int(),
  locations: z.array(z.string()),
  languages: z.array(z.string()),
  keywords: z.array(AdWizardKeyword),
  negatives: z.array(z.string()),
  sharedNegativeIds: z.array(z.string()),
  rsa: z.unknown(),
  sitelinks: z.array(SitelinkInput),
  destinationUrl: z.string().nullable(),
  productKey: z.string().nullable(),
  schedule: AdSchedule.nullable(),
  scheduleReason: AdScheduleReason.nullable(),
  campaignId: Uuid.nullable(),
  errorCode: z.string().nullable(),
  createdAt: IsoDateTime,
});
export type AdWizardDto = z.infer<typeof AdWizardDto>;
export const AdWizardPage = pageOf(AdWizardDto);
export const AdWizardReviewDto = z.object({
  ready: z.boolean(),
  findings: z.array(AdFindingDto),
  combinations: z.number().int(),
  finalUrl: z.string().nullable(),
  urlSuffix: z.string(),
  summary: z.object({ dailyBudgetMinor: z.number().int(), cpcCeilingMinor: z.number().int(), keywords: z.number().int(), negatives: z.number().int(), sitelinks: z.number().int(), startsPaused: z.literal(true) }),
});
export type AdWizardReviewDto = z.infer<typeof AdWizardReviewDto>;

// ---------------------------------------------------------------------------
// Promote in zone
// ---------------------------------------------------------------------------

export const AD_PROMOTION_MIN_RADIUS_KM = 1;
export const AD_PROMOTION_MAX_RADIUS_KM = 80;

export const AdPromotionCreate = z
  .strictObject({
    connectionId: Uuid,
    productKey: z.string().min(1).max(200).optional(),
    offerFactId: Uuid.optional(),
    creativeId: Uuid.optional(),
    radiusKm: z.number().int().min(AD_PROMOTION_MIN_RADIUS_KM).max(AD_PROMOTION_MAX_RADIUS_KM).optional(),
    dailyBudgetMinor: Minor.optional(),
    currency: Currency.optional(),
    durationDays: z.number().int().min(1).max(30).optional(),
    latitude: z.number().min(-90).max(90).optional(),
    longitude: z.number().min(-180).max(180).optional(),
    language: Language.default("ro"),
    destinationUrl: HttpsUrl.optional(),
    /** Left out: prefilled from the sales feed. Null: at all hours. */
    schedule: AdSchedule.nullable().optional(),
  })
  .refine((v) => [v.productKey, v.offerFactId, v.creativeId].filter(Boolean).length === 1, { message: "exactly one of product, offer, creative" })
  .refine((v) => (v.latitude === undefined) === (v.longitude === undefined), { message: "both coordinates or none" });
export const AdPromotionPatch = z.strictObject({
  radiusKm: z.number().int().min(AD_PROMOTION_MIN_RADIUS_KM).max(AD_PROMOTION_MAX_RADIUS_KM).optional(),
  dailyBudgetMinor: Minor.optional(),
  durationDays: z.number().int().min(1).max(30).optional(),
  schedule: AdSchedule.nullable().optional(),
});
export const AdPromotionDto = z.object({
  id: Uuid,
  connectionId: Uuid,
  code: z.string(),
  sourceKind: z.string(),
  sourceRef: z.string(),
  productKey: z.string().nullable(),
  schedule: AdSchedule.nullable(),
  scheduleReason: AdScheduleReason.nullable(),
  status: AdPromotionStatus,
  systemStop: AdSystemStop.nullable(),
  currency: z.string(),
  dailyBudgetMinor: z.number().int(),
  totalBudgetMinor: z.number().int(),
  durationDays: z.number().int(),
  radiusKm: z.number().int(),
  latitude: z.number(),
  longitude: z.number(),
  creative: z.object({ text: z.string(), headline: z.string(), imageUrl: z.string().nullable(), link: z.string() }),
  finalUrl: z.string(),
  findings: z.array(AdFindingDto),
  preview: z.unknown(),
  estimate: z.object({ source: z.string(), lowerReach: z.number().int(), upperReach: z.number().int() }).nullable(),
  campaignId: Uuid.nullable(),
  startedAt: IsoDateTime.nullable(),
  endsAt: IsoDateTime.nullable(),
  errorCode: z.string().nullable(),
  createdAt: IsoDateTime,
});
export type AdPromotionDto = z.infer<typeof AdPromotionDto>;
export const AdPromotionPage = pageOf(AdPromotionDto);

// ---------------------------------------------------------------------------
// Audiences and catalog
// ---------------------------------------------------------------------------

export const AD_AUDIENCE_KINDS = ["customer_list", "lookalike", "visitors"] as const;
export const AdAudienceKind = z.enum(AD_AUDIENCE_KINDS);
export type AdAudienceKind = z.infer<typeof AdAudienceKind>;

const AudienceBase = { connectionId: Uuid, name: Name };
export const AdAudienceCreate = z.discriminatedUnion("kind", [
  z.strictObject({ kind: z.literal("customer_list"), ...AudienceBase, segmentId: Uuid.optional() }),
  z.strictObject({ kind: z.literal("lookalike"), ...AudienceBase, sourceAudienceId: Uuid, country: Country.default("RO"), ratio: z.number().min(0.01).max(0.2).default(0.01) }),
  z.strictObject({ kind: z.literal("visitors"), ...AudienceBase, retentionDays: z.number().int().min(1).max(180).default(30), urlContains: z.string().trim().min(1).max(200).optional() }),
]);
export const AdAudienceDto = z.object({
  id: Uuid,
  connectionId: Uuid,
  kind: AdAudienceKind,
  name: z.string(),
  status: z.string(),
  externalId: z.string().nullable(),
  sourceAudienceId: Uuid.nullable(),
  segmentId: Uuid.nullable(),
  settings: z.unknown(),
  memberCount: z.number().int(),
  skippedNoConsent: z.number().int(),
  lastRefreshAt: IsoDateTime.nullable(),
  errorCode: z.string().nullable(),
  createdAt: IsoDateTime,
});
export type AdAudienceDto = z.infer<typeof AdAudienceDto>;
export const AdAudiencePage = pageOf(AdAudienceDto);
export const AdAudienceRefreshDto = z.object({
  audienceId: Uuid,
  dryRun: z.boolean(),
  added: z.number().int(),
  removed: z.number().int(),
  memberCount: z.number().int(),
  skippedNoConsent: z.number().int(),
});
export type AdAudienceRefreshDto = z.infer<typeof AdAudienceRefreshDto>;

export const AdCatalogCreate = z.strictObject({ connectionId: Uuid, name: Name });
export const AdCatalogDto = z.object({
  id: Uuid,
  connectionId: Uuid,
  name: z.string(),
  status: z.string(),
  externalId: z.string().nullable(),
  itemCount: z.number().int(),
  skipped: z.array(z.object({ key: z.string(), reason: z.string() })),
  lastSyncAt: IsoDateTime.nullable(),
  errorCode: z.string().nullable(),
  createdAt: IsoDateTime,
});
export type AdCatalogDto = z.infer<typeof AdCatalogDto>;
export const AdCatalogPage = pageOf(AdCatalogDto);
export const AdCatalogSyncDto = z.object({
  catalogId: Uuid,
  dryRun: z.boolean(),
  sent: z.number().int(),
  removed: z.number().int(),
  unchanged: z.number().int(),
  skipped: z.array(z.object({ key: z.string(), reason: z.string() })),
});
export type AdCatalogSyncDto = z.infer<typeof AdCatalogSyncDto>;

/**
 * What a new ad on this account costs and starts with, so a screen can state the price before anything is created. These
 * are the amounts the API itself falls back on when a budget is left out.
 */
export const AdConnectionLimitsDto = z.object({
  connectionId: Uuid,
  currency: z.string(),
  /** Per variant, per day. */
  smallestDailyBudgetMinor: z.number().int(),
  defaultCpcCeilingMinor: z.number().int(),
  minVariants: z.number().int(),
  maxVariants: z.number().int(),
  /** A test cell's budget can grow to this many times the first budget, unless a person sets another ceiling. */
  scaleCeilingFactor: z.number().int(),
  autoScale: z.boolean(),
  abcMode: z.enum(["advise", "stop_losers"]),
  defaultRadiusKm: z.number().int(),
  minRadiusKm: z.number().int(),
  maxRadiusKm: z.number().int(),
  defaultDurationDays: z.number().int(),
  /** The least the engine needs before it compares anything. */
  verdict: z.object({ minDays: z.number().int(), minClicksPerVariant: z.number().int(), minImpressionsPerVariant: z.number().int(), minResultsPerCell: z.number().int() }),
});
export type AdConnectionLimitsDto = z.infer<typeof AdConnectionLimitsDto>;

/** A start goes through the guards one entity at a time. `dryRun` means the live switch is off: it stays paused, and the journal says what would have happened. */
export const AdStartResultDto = z.object({ started: z.boolean(), dryRun: z.boolean() });
export type AdStartResultDto = z.infer<typeof AdStartResultDto>;

// ---------------------------------------------------------------------------
// Dynamic product ads: a set of products from the catalog, one template, prices from the feed
// ---------------------------------------------------------------------------

const MinorAmount = z.number().int().nonnegative().max(1_000_000_000_000);
const AD_PRODUCT_MAX_HAND_PICKED = 500;
export const AD_PRODUCT_MESSAGE_MAX = 500;
export const AD_PRODUCT_RETARGETING_RETENTION_DAYS = 30;
export const AD_PRODUCT_RETARGETING_MIN_VISITORS = 100;
export const AD_PRODUCT_EXCLUSION_REASONS = ["no_photo", "no_price", "out_of_stock", "no_link", "other_currency", "not_in_feed"] as const;
export const AdProductExclusionReason = z.enum(AD_PRODUCT_EXCLUSION_REASONS);
export type AdProductExclusionReason = z.infer<typeof AdProductExclusionReason>;

/** By rule or by hand. Being in stock is not a choice: what is out of stock is never in the set. */
export const AdProductSelection = z.discriminatedUnion("kind", [
  z
    .strictObject({
      kind: z.literal("rule"),
      /** Empty means every category. */
      categories: z.array(z.string().trim().min(1).max(200)).max(100),
      priceMinMinor: MinorAmount.optional(),
      priceMaxMinor: MinorAmount.optional(),
    })
    .refine((v) => v.priceMinMinor === undefined || v.priceMaxMinor === undefined || v.priceMinMinor <= v.priceMaxMinor, { path: ["priceMaxMinor"], message: "the lowest price is above the highest" }),
  z.strictObject({ kind: z.literal("hand"), keys: z.array(ProductKey).min(1).max(AD_PRODUCT_MAX_HAND_PICKED) }),
]);
export type AdProductSelection = z.infer<typeof AdProductSelection>;

export const AdProductAudience = z.discriminatedUnion("kind", [
  z.strictObject({ kind: z.literal("nearby"), radiusKm: z.number().int().min(AD_PROMOTION_MIN_RADIUS_KM).max(AD_PROMOTION_MAX_RADIUS_KM), latitude: z.number().min(-90).max(90).optional(), longitude: z.number().min(-180).max(180).optional() }).refine((v) => (v.latitude === undefined) === (v.longitude === undefined), { message: "both coordinates or none" }),
  z.strictObject({ kind: z.literal("country") }),
  z.strictObject({ kind: z.literal("retargeting") }),
]);
export type AdProductAudience = z.infer<typeof AdProductAudience>;

export const AdProductAdCreate = z.strictObject({
  catalogId: Uuid,
  name: Name,
  selection: AdProductSelection,
  audience: AdProductAudience,
  dailyBudgetMinor: Minor.optional(),
  currency: Currency.optional(),
  language: Language.default("ro"),
  message: z.string().trim().min(1).max(AD_PRODUCT_MESSAGE_MAX).optional(),
});
export const AdProductAdPatch = z.strictObject({
  dailyBudgetMinor: Minor.optional(),
  message: z.string().trim().min(1).max(AD_PRODUCT_MESSAGE_MAX).optional(),
});
export const AdProductPreviewInput = z.strictObject({ catalogId: Uuid, selection: AdProductSelection });
export const AdProductOptionsQuery = z.object({ catalogId: Uuid.optional() });

export const AdProductItemDto = z.object({
  key: z.string(),
  name: z.string(),
  category: z.string().nullable(),
  /** The feed's price, and the feed's offer when one is running. Never typed by anyone here. */
  priceMinor: z.number().int(),
  salePriceMinor: z.number().int().nullable(),
  currency: z.string(),
  imageUrl: z.string(),
});
export type AdProductItemDto = z.infer<typeof AdProductItemDto>;
export const AdProductExcludedDto = z.object({ key: z.string(), name: z.string(), reason: AdProductExclusionReason });
export type AdProductExcludedDto = z.infer<typeof AdProductExcludedDto>;
export const AdProductSetDto = z.object({
  currency: z.string(),
  eligibleCount: z.number().int(),
  /** The first ones, to look at. The count above is all of them. */
  items: z.array(AdProductItemDto),
  excludedCounts: z.record(z.string(), z.number().int()),
  excluded: z.array(AdProductExcludedDto),
});
export type AdProductSetDto = z.infer<typeof AdProductSetDto>;

export const AD_PRODUCT_RETARGETING_REFUSALS = ["blocked_by_vertical", "no_pixel", "not_enough_visitors"] as const;
export const AdProductOptionsDto = z.object({
  allowed: z.boolean(),
  /** The page and pixel of the catalog's account, when a catalog was named. */
  hasPage: z.boolean(),
  hasPixel: z.boolean(),
  hasLocation: z.boolean(),
  categories: z.array(z.object({ name: z.string(), count: z.number().int() })),
  products: z.array(z.object({ key: z.string(), name: z.string(), category: z.string().nullable() })),
  retargeting: z.object({
    available: z.boolean(),
    reason: z.enum(AD_PRODUCT_RETARGETING_REFUSALS).nullable(),
    /** Counted up to what is needed: how far it is, not how big the audience is. */
    visitors: z.number().int(),
    needed: z.number().int(),
    retentionDays: z.number().int(),
  }),
});
export type AdProductOptionsDto = z.infer<typeof AdProductOptionsDto>;

export const AdProductAdDto = z.object({
  id: Uuid,
  connectionId: Uuid,
  catalogId: Uuid,
  code: z.string(),
  name: z.string(),
  status: AdPromotionStatus,
  /** With `stopped_by_system`, or `pause_failed` beside `running`: read from the mirror, as for a promotion. */
  systemStop: AdSystemStop.nullable(),
  currency: z.string(),
  dailyBudgetMinor: z.number().int(),
  selection: AdProductSelection,
  audience: AdProductAudience,
  language: Language,
  message: z.string(),
  itemCount: z.number().int(),
  excludedCounts: z.record(z.string(), z.number().int()),
  /** What the choice matched and could not go in, named. */
  excluded: z.array(AdProductExcludedDto),
  /** The first products of the set, read from the feed now. Only on the one ad, not in a list. */
  items: z.array(AdProductItemDto),
  findings: z.array(AdFindingDto),
  /** One product of the set laid out as the ad shows it. Null when the set is empty or in a list. */
  preview: z.unknown(),
  campaignId: Uuid.nullable(),
  startedAt: IsoDateTime.nullable(),
  errorCode: z.string().nullable(),
  createdAt: IsoDateTime,
});
export type AdProductAdDto = z.infer<typeof AdProductAdDto>;
export const AdProductAdPage = pageOf(AdProductAdDto);
